import { graphql } from "gatsby";
import React from "react";
import { twJoin } from "tailwind-merge";

import RichText from "../richText";
import Video from "../video";

type Props = {
  slice:
    | Queries.TreatmentMuxVideoWithTextSliceFragment
    | Queries.TreatmentAddonMuxVideoWithTextSliceFragment
    | Queries.MarketingTreatmentMuxVideoWithTextSliceFragment
    | Queries.ContentMuxVideoWithTextSliceFragment
    | Queries.DentistMuxVideoWithTextSliceFragment
    | Queries.LocationMuxVideoWithTextSliceFragment
    | Queries.ProductMuxVideoWithTextSliceFragment;
};

const MuxVideoWithText: React.FC<Props> = ({ slice }) => {
  const { primary } = slice;
  const { title, text, side, playbackID, bgGrey, margins } = primary;

  if (!playbackID) return null;

  return (
    <section className={twJoin("px-5", bgGrey ? "bg-neutral-50" : "bg-white")}>
      <div
        className={twJoin(
          "container px-0 max-w-screen-xl grid mdlg:grid-cols-2 gap-5 mdlg:gap-10 lg:gap-[70px]",
          margins === "Only bottom" && "p-b-xxxl-fluid",
          margins === "Only top" && "p-t-xxxl-fluid",
          margins === "Top and bottom" && "p-b-xxxl-fluid p-t-xxxl-fluid",
        )}
      >
        <div
          className={twJoin(
            "relative row-start-1 overflow-hidden rounded-lg empty:hidden mdlg:row-start-auto",
            side === "left" ? "mdlg:order-0" : "mdlg:order-1",
          )}
        >
          <Video
            id={playbackID}
            className="absolute flex min-h-full !min-w-fit items-center justify-center"
            classNameWrapper="aspect-[4/3] md:aspect-[5/4] !w-full !flex"
          />
        </div>
        <div
          className={twJoin(
            "self-center lg:max-w-[565px]",
            side === "left" ? "mdlg:justify-self-start" : "mdlg:justify-self-end",
          )}
        >
          <RichText data={title?.richText} className="block m-b-sm-fluid" />
          <RichText data={text?.richText} className="block typo-base unstyled-list text-neutral-600" />
        </div>
      </div>
    </section>
  );
};

export const query = graphql`
  fragment TreatmentMuxVideoWithTextSlice on PrismicTreatmentDataBodyMuxVideoWithText {
    ...Slice

    primary {
      title: mux_video_title {
        ...RichText
      }
      text: mux_video_text {
        ...RichText
      }
      side: mux_video_side
      playbackID: mux_video_playback_id
      bgGrey: mux_video_background_color
      margins: mux_video_margins
    }
  }

  fragment TreatmentAddonMuxVideoWithTextSlice on PrismicTreatmentAddonDataBodyMuxVideoWithText {
    ...Slice

    primary {
      title: mux_video_title {
        ...RichText
      }
      text: mux_video_text {
        ...RichText
      }
      side: mux_video_side
      playbackID: mux_video_playback_id
      bgGrey: mux_video_background_color
      margins: mux_video_margins
    }
  }

  fragment MarketingTreatmentMuxVideoWithTextSlice on PrismicMarketingTreatmentDataBodyMuxVideoWithText {
    ...Slice

    primary {
      title: mux_video_title {
        ...RichText
      }
      text: mux_video_text {
        ...RichText
      }
      side: mux_video_side
      playbackID: mux_video_playback_id
      bgGrey: mux_video_background_color
      margins: mux_video_margins
    }
  }

  fragment ContentMuxVideoWithTextSlice on PrismicPageContentDataBodyMuxVideoWithText {
    ...Slice

    primary {
      title: mux_video_title {
        ...RichText
      }
      text: mux_video_text {
        ...RichText
      }
      side: mux_video_side
      playbackID: mux_video_playback_id
      bgGrey: mux_video_background_color
      margins: mux_video_margins
    }
  }

  fragment DentistMuxVideoWithTextSlice on PrismicPageDentistDataBodyMuxVideoWithText {
    ...Slice

    primary {
      title: mux_video_title {
        ...RichText
      }
      text: mux_video_text {
        ...RichText
      }
      side: mux_video_side
      playbackID: mux_video_playback_id
      bgGrey: mux_video_background_color
      margins: mux_video_margins
    }
  }

  fragment LocationMuxVideoWithTextSlice on PrismicLocationDataBodyMuxVideoWithText {
    ...Slice

    primary {
      title: mux_video_title {
        ...RichText
      }
      text: mux_video_text {
        ...RichText
      }
      side: mux_video_side
      playbackID: mux_video_playback_id
      bgGrey: mux_video_background_color
      margins: mux_video_margins
    }
  }

  fragment ProductMuxVideoWithTextSlice on PrismicProductDataBodyMuxVideoWithText {
    ...Slice

    primary {
      title: mux_video_title {
        ...RichText
      }
      text: mux_video_text {
        ...RichText
      }
      side: mux_video_side
      playbackID: mux_video_playback_id
      bgGrey: mux_video_background_color
      margins: mux_video_margins
    }
  }
`;

export default MuxVideoWithText;
